<template>
  <div class="app-container">
    <div class="filter-container">
      <el-input clearable v-model="listQuery.account_name" :placeholder="$t('publicAccount.publicAccountName')" style="width: 200px;" class="filter-item" @keyup.enter.native="handleFilter" />
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
        {{ $t('table.search') }}
      </el-button>
      <el-button class="filter-item" style="margin-left: 10px;" type="primary" icon="el-icon-edit" @click="handleCreate">
        {{ $t('table.add') }}
      </el-button>
    </div>

    <el-table
      :key="tableKey"
      v-loading="listLoading"
      :data="list"
      border
      fit
      highlight-current-row
      style="width: 100%;"
      @sort-change="sortChange"
    >
      <el-table-column :label="$t('table.id')" prop="id" sortable="custom" align="center" width="100px">
        <template slot-scope="scope">
          <span>{{ scope.row.id }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('publicAccount.publicAccountName')" align="center" min-width="180px">
        <template slot-scope="scope">
          <span>{{ scope.row.account_name }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('publicAccount.coverPicture')" align="center" width="150px">
        <template slot-scope="scope">
          <el-image
            style="width: 50px; height: 50px"
            :src="$publicData.imgUrl + scope.row.cover"
            :preview-src-list="[$publicData.imgUrl + scope.row.cover]">
          </el-image>
        </template>
      </el-table-column>
      <el-table-column label="biz" align="center" min-width="180px">
        <template slot-scope="scope">
          <span>{{ scope.row.biz }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('publicAccount.mustPayAttentionTo')" align="center" width="170px">
        <template slot-scope="scope">
          <el-switch
            v-model="scope.row.is_must_attention"
            active-color="#13ce66"
            inactive-color="#ff4949"
            :active-value="1"
            :inactive-value="2"
            @change="isMustAttentionChange($event, scope.row)">
          </el-switch>
        </template>
      </el-table-column>
      <el-table-column :label="$t('table.status')" align="center" width="100px">
        <template slot-scope="scope">
          <el-switch
            v-model="scope.row.status"
            active-color="#13ce66"
            inactive-color="#ff4949"
            :active-value="1"
            :inactive-value="2"
            @change="statusChange($event, scope.row)">
          </el-switch>
        </template>
      </el-table-column>
      <el-table-column :label="$t('table.sort')" align="center" width="200px">
        <template slot-scope="scope">
          <el-input-number size="small" v-model="scope.row.sort_index" @change="setSort($event, $event, scope.row)" :min="0"></el-input-number>
        </template>
      </el-table-column>
      <el-table-column :label="$t('table.actions')" fixed="right" align="center" width="230" class-name="small-padding fixed-width">
        <template slot-scope="scope">
          <el-button type="primary" size="mini" @click="handleUpdate(scope.row)">
            {{ $t('table.edit') }}
          </el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">
            {{ $t('table.delete') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination v-show="total>0" :total="total" :page.sync="listQuery.page" :limit.sync="listQuery.limit" @pagination="getList" />

    <el-dialog :title="textMap" :visible.sync="dialogFormVisible">
      <el-form ref="dataForm" :rules="rules" :model="temp" label-position="right" :label-width="formLabelWidth" v-loading="dialogFormLoading">
        <el-form-item :label="$t('publicAccount.publicAccountName')" prop="account_name">
          <el-input v-model="temp.account_name" />
        </el-form-item>
        <el-form-item :label="$t('publicAccount.cover')" prop="cover">
          <image-upload ref="imageUpload" :image-list="imageDefaultList" @imageAdd="imageAdd" @imageRemove="imageRemove"></image-upload>
        </el-form-item>
        <el-form-item label="biz" prop="biz">
          <el-input v-model="temp.biz" />
        </el-form-item>
        <el-form-item :label="$t('publicAccount.mustPayAttentionTo')">
          <el-switch
            v-model="temp.is_must_attention"
            active-color="#13ce66"
            inactive-color="#ff4949"
            :active-value="1"
            :inactive-value="2">
          </el-switch>
        </el-form-item>
        <el-form-item :label="$t('table.status')">
          <el-switch
            v-model="temp.status"
            active-color="#13ce66"
            inactive-color="#ff4949"
            :active-value="1"
            :inactive-value="2">
          </el-switch>
        </el-form-item>
        <el-form-item :label="$t('table.sort')">
          <el-input-number v-model="temp.sort_index" :min="0"></el-input-number>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogFormVisible = false">
          {{ $t('table.cancel') }}
        </el-button>
        <el-button type="primary" @click="dialogStatus==='create'?createData():updateData()">
          {{ $t('table.confirm') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchList, fetchPublicAccount, createPublicAccount, updatePublicAccount, delPublicAccount, editStatus, editIsMustAttention, editSort } from '@/api/publicAccount'
import waves from '@/directive/waves' // Waves directive
import Pagination from '@/components/Pagination' // Secondary package based on el-pagination
import ImageUpload from '@/components/Upload/image'

export default {
  name: 'IndexPublicAccount',
  components: { Pagination, ImageUpload },
  directives: { waves },
  data() {
    var validateCover = (rule, value, callback) => {
      if (value === '') {
        callback(new Error(this.$t('publicAccount.coverRequired')))
      } else {
        callback()
      }
    }
    var validateBiz = (rule, value, callback) => {
      if (value === '') {
        callback(new Error(this.$t('publicAccount.bizRequired')))
      } else {
        callback()
      }
    }
    var validateAccountName = (rule, value, callback) => {
      if (value === '') {
        callback(new Error(this.$t('publicAccount.publicAccountNameRequired')))
      } else {
        callback()
      }
    }
    return {
      tableKey: 0,
      list: null,
      total: 0,
      listLoading: true,
      listQuery: {
        page: 1,
        limit: 20,
        account_name: undefined,
        id_sort: 'desc'
      },
      temp: {
        id: undefined,
        account_name: '',
        cover: '',
        biz: '',
        is_must_attention: 2,
        status: 1,
        sort_index: 0
      },
      dialogFormVisible: false,
      dialogStatus: '',
      rules: {
        account_name: [{ required: true, validator: validateAccountName, trigger: 'blur' }],
        cover: [{ required: true, validator: validateCover, trigger: 'change' }],
        biz: [{ required: true, validator: validateBiz, trigger: 'blur' }]
      },
      imageDefaultList: '',
      dialogFormLoading: false
    }
  },
  computed: {
    formLabelWidth: function() {
      return this.$store.getters.language === 'zh' ? '100px' : '200px'
    },
    textMap: function() {
      return this.dialogStatus === 'update' ? this.$t('form.edit') : this.$t('form.create')
    }
  },
  created() {
    this.getList()
  },
  methods: {
    imageAdd(file) {
      this.temp.cover = file.path
    },
    imageRemove(file) {
      this.temp.cover = ''
    },
    setSort(currentValue, oldValue, row) {
      editSort({ id: row.id, sort_index: currentValue }).then((res) => {
        if (res.code === 20000) {
          this.$message.success(this.$t('form.updateSuccess'))
        }
      })
    },
    getList() {
      this.listLoading = true
      fetchList(this.listQuery).then(response => {
        this.list = response.data.list
        this.total = response.data.total
        this.listLoading = false
      })
    },
    handleFilter() {
      this.listQuery.page = 1
      this.getList()
    },
    sortChange(data) {
      const { prop, order } = data
      if (prop === 'id') {
        this.sortByID(order)
      }
    },
    sortByID(order) {
      if (order === 'ascending') {
        this.listQuery.id_sort = 'asc'
      } else {
        this.listQuery.id_sort = 'desc'
      }
      this.handleFilter()
    },
    resetTemp() {
      this.temp = {
        id: undefined,
        account_name: '',
        cover: '',
        biz: '',
        is_must_attention: 2,
        status: 1,
        sort_index: 0
      }
      this.imageDefaultList = ''
    },
    handleCreate() {
      this.resetTemp()
      this.dialogStatus = 'create'
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate()
        this.$refs['imageUpload'].clearFileList()
      })
    },
    createData() {
      this.$refs['dataForm'].validate((valid) => {
        if (valid) {
          createPublicAccount(this.temp).then((res) => {
            if (res.code === 20000) {
              this.dialogFormVisible = false
              this.$message.success(this.$t('form.createSuccess'))
              this.handleFilter()
            }
          })
        }
      })
    },
    handleUpdate(row) {
      this.dialogFormLoading = true
      this.resetTemp()
      this.dialogStatus = 'update'
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate()
        this.$refs['imageUpload'].clearFileList()
      })
      fetchPublicAccount(row.id).then((res) => {
        var path = res.data.detail.cover
        this.temp = {
          id: res.data.detail.id,
          account_name: res.data.detail.account_name,
          cover: path,
          biz: res.data.detail.biz,
          is_must_attention: res.data.detail.is_must_attention,
          status: res.data.detail.status,
          sort_index: res.data.detail.sort_index
        }
        this.imageDefaultList = JSON.stringify([
          {
            name: path.substr(path.lastIndexOf('/') + 1),
            url: this.$publicData.imgUrl + path,
            path: path
          }
        ])
        this.dialogFormLoading = false
      })
    },
    updateData() {
      this.$refs['dataForm'].validate((valid) => {
        if (valid) {
          updatePublicAccount(this.temp).then(() => {
            this.dialogFormVisible = false
            this.$message.success(this.$t('form.updateSuccess'))
            this.handleFilter()
          })
        }
      })
    },
    handleDelete(row) {
      this.$confirm('此操作将永久删除该数据, 是否继续?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        delPublicAccount({ id: row.id }).then((res) => {
          if (res.code === 20000) {
            this.$message.success(this.$t('form.delSuccess'))
            this.handleFilter()
          }
        })
      }).catch(() => {
      })
    },
    isMustAttentionChange(val, row) {
      editIsMustAttention({ id: row.id, is_must_attention: val }).then((res) => {
        if (res.code === 20000) {
          this.$message.success(this.$t('form.updateSuccess'))
        }
      })
    },
    statusChange(val, row) {
      editStatus({ id: row.id, status: val }).then((res) => {
        if (res.code === 20000) {
          this.$message.success(this.$t('form.updateSuccess'))
        }
      })
    }
  }
}
</script>
